import { TCmsFlow } from '../models/flow.ts';
import { findOne } from '../payload/client.ts';
import { normalizeFlow } from '../payload/flow.ts';
import { MEDIA_POPULATE } from '../payload/media.ts';
import { TPayloadFlow } from '../payload/types.ts';
import { fetchStaticConfig } from '../staticConfig.ts';

const COLLECTION = 'registration_flows';

const FLOW_DEPTH = 2;

const flowFilter = (flowName: string) => ({ 'where[name][equals]': flowName });

const getStaticFlowConfig = async ({
  flowName,
  language,
}: {
  flowName: string;
  language: string;
}): Promise<TCmsFlow | undefined> => {
  try {
    const { docs } = await fetchStaticConfig({ flowName, language });

    return docs?.[0] ? normalizeFlow(docs[0]) : undefined;
  } catch (error) {
    console.error('Error loading fallback config:', error);

    return undefined;
  }
};

/**
 * Fetches the raw flow definition for a market/language. Falls back to the
 * statically published JSON config when the CMS query fails or returns nothing.
 *
 * Returns the raw, Payload-shaped flow. The structural mapping into `TFlow`,
 * {{variable}} interpolation, and user-location filtering are owned by the
 * render layer (see src/cms/README.md).
 */
export const getFlowConfig = async ({
  flowName,
  language,
}: {
  flowName: string;
  language: string;
}): Promise<TCmsFlow | undefined> => {
  try {
    const flow = await findOne<TPayloadFlow>(COLLECTION, {
      locale:   language,
      depth:    FLOW_DEPTH,
      populate: MEDIA_POPULATE,
      where:    flowFilter(flowName),
    });

    if (!flow) {
      throw new Error(`No "${flowName}" flow for locale ${language}`);
    }

    return normalizeFlow(flow);
  } catch (error) {
    console.error('CMS flow fetch failed, trying static JSON:', error);

    return getStaticFlowConfig({ flowName, language });
  }
};
